import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Group as GroupIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  DateRange as DateRangeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

// Dashboard card for summary metrics
const MetricCard = ({ title, value, subtitle, icon, trend, trendValue }) => {
  const Icon = icon;
  const isPositive = trend === 'up';
  const TrendIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;
  
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ 
            backgroundColor: 'action.hover', 
            borderRadius: '50%', 
            p: 1, 
            display: 'flex' 
          }}>
            <Icon color="primary" />
          </Box>
        </Stack>
        
        <Typography variant="h4" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
          
          {trend && trendValue && (
            <Chip
              icon={<TrendIcon fontSize="small" />}
              label={`${trendValue}%`}
              size="small"
              color={isPositive ? 'success' : 'error'}
              sx={{ height: 24 }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Main component for admin analytics
function AdminAnalytics() {
  // State
  const [timeRange, setTimeRange] = useState('last30days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Calculate date range based on selected time period
  const dateRange = useMemo(() => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);
    
    switch (timeRange) {
      case 'last7days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(today.getDate() - 30);
        break;
      case 'last90days':
        startDate.setDate(today.getDate() - 90);
        break;
      case 'thisYear':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case 'lastYear':
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate.setFullYear(today.getFullYear() - 1, 11, 31);
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }
    
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }, [timeRange]);
  
  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get('/api/admin/analytics', {
          params: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          }
        });
        
        if (response.data.status === 'success') {
          setAnalytics(response.data.data);
          console.log('analytics', response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch analytics data');
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch analytics data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [dateRange]);
  
  // Handler for time range change
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  // Handle CSV export
  const handleExport = async () => {
    try {
      const response = await axios.get('/api/admin/analytics/export', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          format: 'csv'
        },
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to export analytics data');
    }
  };
  
  // Mock data for display when real data is not available
  const mockData = {
    summary: {
      revenue: {
        current: '€4,350',
        previous: '€3,980',
        trend: 'up',
        percentage: 9.3
      },
      users: {
        current: '127',
        previous: '118',
        trend: 'up',
        percentage: 7.6
      },
      apiCalls: {
        current: '243,756',
        previous: '198,432',
        trend: 'up',
        percentage: 22.8
      },
      conversionRate: {
        current: '8.7%',
        previous: '7.2%',
        trend: 'up',
        percentage: 20.8
      }
    },
    revenueByTier: [
      { name: 'Free', value: 0 },
      { name: 'Professional', value: 2950 },
      { name: 'Enterprise', value: 1400 }
    ],
    usersByTier: [
      { name: 'Free', value: 95 },
      { name: 'Professional', value: 22 },
      { name: 'Enterprise', value: 10 }
    ],
    apiCallsByType: [
      { name: 'Retrieval', value: 142568 },
      { name: 'Submission', value: 67832 },
      { name: 'Amendment', value: 22187 },
      { name: 'Retraction', value: 11169 },
      { name: 'Echo', value: 5555 }
    ],
    revenueOverTime: [
      { month: 'Jan', revenue: 3200 },
      { month: 'Feb', revenue: 3400 },
      { month: 'Mar', revenue: 3800 },
      { month: 'Apr', revenue: 4100 },
      { month: 'May', revenue: 4350 }
    ],
    apiCallsOverTime: [
      { month: 'Jan', calls: 178000 },
      { month: 'Feb', calls: 185000 },
      { month: 'Mar', calls: 202000 },
      { month: 'Apr', calls: 226000 },
      { month: 'May', calls: 243000 }
    ],
    tierConversion: {
      freeToProf: 4.8,
      profToEnterprise: 1.9
    }
  };
  
  // Use real data if available, otherwise use mock data
  const data = analytics || mockData;
  
  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  return (
    <Box>
      {/* Header with time range selector */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" component="h2">
          Business Analytics Dashboard
        </Typography>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
              startAdornment={<DateRangeIcon sx={{ mr: 1, ml: -0.5 }} />}
            >
              <MenuItem value="last7days">Last 7 days</MenuItem>
              <MenuItem value="last30days">Last 30 days</MenuItem>
              <MenuItem value="last90days">Last 90 days</MenuItem>
              <MenuItem value="thisYear">This year</MenuItem>
              <MenuItem value="lastYear">Last year</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key metrics */}
          <Stack direction="row" spacing={3} sx={{ mb: 4, flexWrap: 'wrap' }}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
              <MetricCard
                title="Revenue"
                value={data.summary.revenue.current}
                subtitle="Monthly recurring revenue"
                icon={TrendingUpIcon}
                trend={data.summary.revenue.trend}
                trendValue={data.summary.revenue.percentage}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
              <MetricCard
                title="Users"
                value={data.summary.users.current}
                subtitle="Total active users"
                icon={GroupIcon}
                trend={data.summary.users.trend}
                trendValue={data.summary.users.percentage}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
              <MetricCard
                title="API Calls"
                value={data.summary.apiCalls.current}
                subtitle="Total API calls"
                icon={BarChartIcon}
                trend={data.summary.apiCalls.trend}
                trendValue={data.summary.apiCalls.percentage}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
              <MetricCard
                title="Conversion Rate"
                value={data.summary.conversionRate.current}
                subtitle="Free to paid conversion"
                icon={PieChartIcon}
                trend={data.summary.conversionRate.trend}
                trendValue={data.summary.conversionRate.percentage}
              />
            </Box>
          </Stack>
          
          {/* Revenue charts */}
          <Typography variant="h6" gutterBottom>
            Revenue Analysis
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 6 }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(66.67% - 12px)' } }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Revenue Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={data.revenueOverTime}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis 
                      tickFormatter={(value) => `€${value}`}
                    />
                    <Tooltip 
                      formatter={(value) => [`€${value}`, 'Revenue']}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#8884d8" 
                      activeDot={{ r: 8 }}
                      name="Monthly Revenue"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 12px)' } }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Revenue by Tier
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.revenueByTier}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.revenueByTier.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`€${value}`, 'Revenue']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </Stack>
         
          {/* Usage and user analysis */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Usage & User Analysis
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(33.33% - 12px)' } }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Users by Tier
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.usersByTier}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.usersByTier.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
            <Box sx={{ width: { xs: '100%', md: 'calc(66.67% - 12px)' } }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  API Calls by Type
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={data.apiCallsByType}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#82ca9d" name="API Calls" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </Stack>
          
          {/* Conversion metrics */}
          <Box sx={{ mt: 8 }}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tier Conversion Metrics
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="primary">
                    {data.tierConversion.freeToProf}%
                  </Typography>
                  <Typography variant="body1">
                    Free to Professional Conversion Rate
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="secondary">
                    {data.tierConversion.profToEnterprise}%
                  </Typography>
                  <Typography variant="body1">
                    Professional to Enterprise Conversion Rate
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
}

export default AdminAnalytics; 